import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { TextField, Textarea, ToggleField } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  ConfirmModal,
  FormActions,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { queryErrorMessage } from '~/modules/query';
import { useDeleteProductMutation, useSaveProductMutation } from '~/modules/query/featureFlags';

import { PRODUCT_KEY_RE } from '~/components/Constants/featureFlagsConstants';
import AudienceEditor from '~/components/pages/feature-flags/AudienceEditor';
import { cleanConfig, errorText } from '~/components/pages/feature-flags/common';
import type { EnvConfig, Product } from '~/components/pages/feature-flags/types';
import { useReference } from '~/components/pages/feature-flags/useReference';
import { routes } from '~/app/routes';

const SUBMISSION_LABELS = { on: 'Allow', off: 'Block' };
const DEFAULT_POLICY: EnvConfig = { enabled: true, defaultValue: true, rules: [] };
const KEY_MESSAGE = 'Use lowercase letters, numbers and dashes, starting with a letter or number.';
const FORM_ID = 'product-form';
const LIST = routes.featureFlagsAdmin('products');

const toDraft = (p: Product | null): Product => ({
  key: p?.key ?? '',
  name: p?.name ?? '',
  description: p?.description ?? '',
  submissionPolicy: p?.submissionPolicy ?? DEFAULT_POLICY,
});

const toPayload = (d: Product): Product => ({
  key: d.key.trim(),
  name: d.name.trim(),
  description: d.description.trim(),
  submissionPolicy: cleanConfig(d.submissionPolicy),
});

/** Create (`/feature-flags/admin/products/new`) and edit (`/feature-flags/admin/products/:id`) one product. The subtree is behind the feature flags access gate (FeatureFlag purview). */
export default function ProductPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const ref = useReference();
  const { products, attributes } = ref.reference;
  const record = products.find((p) => p.key === id);
  const notFound = ref.loaded && !isNew && !record && !ref.reference.pending;

  // The API exposes no product version, so a record reseeds on id only; `markSaved` after a save.
  const { draft, patch, dirty, markSaved } = useRecordDraft<Product | null, Product>(
    ref.loaded ? (isNew ? null : record) : undefined,
    { id: id ?? 'new', toDraft },
  );
  const guard = useUnsavedGuard(dirty);

  const [deleting, setDeleting] = useState(false);
  const [saveProduct] = useSaveProductMutation();
  const [deleteProduct] = useDeleteProductMutation();

  const schema = z.object({
    name: z.string().trim().min(1, 'Enter a name.'),
    key: isNew ? z.string().trim().regex(PRODUCT_KEY_RE, KEY_MESSAGE) : z.string(),
  });
  const check = useFormValidation(schema, draft, (field) => `product-${field}`);

  const save = useRecordSave<Product>({
    save: (d) => saveProduct({ data: toPayload(d), existing: !isNew }).unwrap(),
    success: isNew ? 'Product created' : 'Product saved',
    failure: 'The product could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !check.submit()) return;
    if (!(await save.submit(draft))) return;
    if (!isNew) return markSaved(draft);
    guard.release();
    navigate(routes.featureFlagsAdminRecord('products', draft.key.trim()), { replace: true });
  };

  const remove = () => {
    if (!record) return;
    deleteProduct(record.key)
      .unwrap()
      .then(() => {
        toast.success('Product deleted');
        guard.release();
        navigate(LIST, { replace: true });
      })
      .catch((err) =>
        toast.error(
          errorText(
            err,
            'The product could not be deleted. It may still be used by flags, updates, requests or users.',
          ),
        ),
      );
    setDeleting(false);
  };

  const title = isNew ? 'Create product' : (record?.name ?? 'Product');
  const failed = !ref.loaded && !!ref.error;

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'Feature flags', href: routes.featureFlags },
        { label: 'Admin' },
        { label: 'Products', href: LIST },
        { label: title },
      ]}
      actions={
        record
          ? [
              {
                label: 'Delete product',
                kind: 'danger',
                testId: 'product-delete',
                startIcon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setDeleting(true),
              },
            ]
          : undefined
      }
      loading={!draft}
      notFound={notFound}
      notFoundTitle="Product not found"
      backTo={LIST}
      backLabel="Back to products"
      error={failed ? { what: 'the product', message: queryErrorMessage(ref.error) } : null}
      onRetry={ref.refetch}
      guard={guard}
      testId="product"
    >
      {draft && (
        <form id={FORM_ID} noValidate onSubmit={submit}>
          <Card>
            <CardBody className="space-y-6">
              {save.error && <Alert variant="error" message={save.error} />}
              <CardSection title="Details">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <TextField
                    id="product-name"
                    testId="product-name"
                    label="Name"
                    required
                    value={draft.name}
                    onChange={(name) => patch({ name })}
                    onBlur={() => check.touch('name')}
                    error={check.errors.name}
                    placeholder="Office"
                  />
                  <TextField
                    id="product-key"
                    testId="product-key"
                    label="Product key"
                    required
                    code
                    readOnly={!isNew}
                    value={draft.key}
                    onChange={(key) => patch({ key: key.toLowerCase() })}
                    onBlur={() => check.touch('key')}
                    error={check.errors.key}
                    placeholder="office"
                    helperText={
                      isNew
                        ? 'Lowercase letters, numbers and dashes. This key cannot be changed later.'
                        : 'This key is permanent and connects your apps to this product.'
                    }
                  />
                  <div className="lg:col-span-2">
                    <Textarea
                      id="product-description"
                      testId="product-description"
                      label="Description"
                      rows={3}
                      maxLength={400}
                      value={draft.description}
                      onChange={(description) => patch({ description })}
                      placeholder="A short description of this product"
                      helperText="Optional."
                    />
                  </div>
                </div>
              </CardSection>

              <CardSection
                title="Who can submit suggestions"
                description="Uses the same targeting rules as feature flags. These settings apply to this product across all environments."
              >
                <div className="space-y-4">
                  <ToggleField
                    id="product-submissions"
                    testId="product-submissions"
                    label="Submissions"
                    text={
                      draft.submissionPolicy.enabled
                        ? 'Accepting new suggestions'
                        : 'Not accepting new suggestions'
                    }
                    checked={draft.submissionPolicy.enabled}
                    onChange={(enabled) =>
                      patch({ submissionPolicy: { ...draft.submissionPolicy, enabled } })
                    }
                  />
                  <AudienceEditor
                    idPrefix="product-audience"
                    value={draft.submissionPolicy}
                    onChange={(submissionPolicy) => patch({ submissionPolicy })}
                    attributes={attributes}
                    products={products}
                    labels={SUBMISSION_LABELS}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    New suggestions start as Submitted and need approval before they appear to
                    customers.
                  </p>
                </div>
              </CardSection>
            </CardBody>
            <CardFooter>
              <FormActions
                form={FORM_ID}
                discardHref={LIST}
                {...(isNew
                  ? { saveLabel: 'Create product', busyLabel: 'Creating…' }
                  : { disabled: !dirty, disabledReason: 'No changes to save' })}
                busy={save.busy}
                saveTestId="product-save"
              />
            </CardFooter>
          </Card>
        </form>
      )}

      <ConfirmModal
        isOpen={deleting}
        title="Delete product"
        message={`Delete product '${record?.name}'?`}
        confirmLabel="Delete product"
        variant="danger"
        dangerNote="The product and its submission rules are removed for good. A product that flags, updates, requests or users still use cannot be deleted."
        testId="product-delete-confirm"
        onClose={() => setDeleting(false)}
        onConfirm={remove}
      />
    </RecordPageShell>
  );
}
